import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { NgStyle, NgSwitch, NgSwitchCase, NgClass } from '@angular/common';
import { NzSpaceCompactItemDirective } from 'ng-zorro-antd/space';
import { NzSelectComponent, NzOptionComponent } from 'ng-zorro-antd/select';
import { FormsModule } from '@angular/forms';
import { NzButtonComponent } from 'ng-zorro-antd/button';
import { NzWaveDirective } from 'ng-zorro-antd/core/wave';
import { ɵNzTransitionPatchDirective } from 'ng-zorro-antd/core/transition-patch';
import { NzEmptyComponent } from 'ng-zorro-antd/empty';
import { TranslatePipe } from '@ngx-translate/core';

export interface FiltersConfig {
  filterTitle?: string;
  width?: string;
  icon?: string;
  queryName: string;
  subCode?: string;
  value?: string;
  selectList: any[];
  inputType: string;
  selectSize?: 'large' | 'default' | 'small';
  multiSelect?: boolean;
  translated?: boolean;
  property?: string;
  maxTags?: number;
  minTags?: number;
  defaultValue?: any;
  radioValue?: string;
  selectedItems?: any[];
  order?: number;
}

export enum FilterInputs {
  multiSelect = 'multi-select',
  dropdown = 'dropdown',
  singleSelect = 'single-select',
}

@Component({
    selector: 'dynamica-filter',
    templateUrl: './filter.component.html',
    styleUrls: ['./filter.component.scss'],
    imports: [
        NgStyle,
        NgSwitch,
        NgSwitchCase,
        NzSpaceCompactItemDirective,
        NzSelectComponent,
        FormsModule,
        NzOptionComponent,
        NzButtonComponent,
        NzWaveDirective,
        ɵNzTransitionPatchDirective,
        NgClass,
        NzEmptyComponent,
        TranslatePipe,
    ],
})
export class FilterComponent implements OnInit, OnChanges {
  @Input() filterConfig: FiltersConfig;
  @Input() clearFilter = false;

  @Output() filterChange = new EventEmitter<any>();
  @Output() filterClearEmit = new EventEmitter<string>();

  selectedOptions: any = [];

  ngOnInit(): void {
    if (this.filterConfig?.defaultValue != null) {
      this.selectedOptions = this.filterConfig.multiSelect
        ? [this.filterConfig.defaultValue]
        : this.filterConfig.defaultValue;

      this.emitValue(this.selectedOptions);
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.clearFilter?.currentValue) {
      this.selectedOptions = this.filterConfig.multiSelect ? [] : null;
    }
  }

  changeSelect(value: any): void {
    if (!this.filterConfig.multiSelect || !Array.isArray(value)) {
      this.selectedOptions = value;
      this.emitValue(value);
      return;
    }

    const allValues = this.getAllValues();
    const cleanValues = value.filter((v) => v !== 'all');
    const hadAll = this.selectedOptions?.includes('all');
    const hasAllNow = value.includes('all');
    if (hasAllNow && !hadAll) {
      Promise.resolve().then(() => {
        this.selectedOptions = ['all', ...allValues];
        this.emitValue(allValues);
      });
      return;
    }

    if (!hasAllNow && hadAll && cleanValues.length === allValues.length) {
      Promise.resolve().then(() => {
        this.selectedOptions = [];
        this.emitValue([]);
      });
      return;
    }

    if (hadAll && hasAllNow) {
      Promise.resolve().then(() => {
        this.selectedOptions = cleanValues;
        this.emitValue(cleanValues);
      });
      return;
    }

    if (cleanValues.length === allValues.length) {
      Promise.resolve().then(() => {
        this.selectedOptions = ['all', ...allValues];
        this.emitValue(allValues);
      });

      return;
    }

    this.selectedOptions = cleanValues;
    this.emitValue(cleanValues);
  }

  toggleMultiButton(value: any): void {
    if (!Array.isArray(this.selectedOptions)) {
      this.selectedOptions = [];
    }

    if (this.selectedOptions.includes(value)) {
      this.selectedOptions = this.selectedOptions.filter((v) => v !== value);
    } else {
      this.selectedOptions = [...this.selectedOptions, value];
    }

    this.emitValue(this.selectedOptions);
  }

  onClearSelect(name: string): void {
    this.filterClearEmit.emit(name);
  }

  getSelectedOptionValue(): string {
    if (!this.selectedOptions?.length) {
      return '';
    }

    const selectedItem = this.filterConfig.selectList.find(
      (item: any) =>
        item[this.filterConfig.property] === this.selectedOptions[0],
    );

    return selectedItem?.name || '';
  }

  private emitValue(value: any): void {
    const payload: any = {};
    const cleanValue = Array.isArray(value)
      ? value.filter((v) => v !== 'all')
      : value;

    payload[this.filterConfig.queryName] = cleanValue;
    this.filterChange.emit(payload);
  }

  private getAllValues(): any[] {
    return this.filterConfig.selectList.map((item: any) =>
      String(item[this.filterConfig.property]),
    );
  }
}
